import { CommonModule } from "@angular/common";
import { AfterViewInit, Component, computed, ElementRef, inject, signal, ViewChild, OnDestroy } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { TranslocoDirective } from "@jsverse/transloco";
import { LucideAngularModule, Menu } from "lucide-angular";
import { InputSearchDropdownUifComponent } from "../../../input-search-dropdown-uif/input-search-dropdown-uif.component";
import { ShellNavitemUifComponent } from "../shell-navitem-uif/shell-navitem-uif.component";
import { NavService } from "./NavService";
import { ShellService } from "../../../../services/shell.service";
import { NavDto } from "./NavDto";
import { HomeRouteDefs } from "../../../../../features/home/routing/HomeRouteDefs";
import { PageErrorUifComponent } from "../../../page-error-uif/page-error-uif.component";
import { ShellBreadcrumbsUifComponent } from "../shell-breadcrumbs-uif/shell-breadcrumbs-uif.component";
import { AccessControlService } from "../../../../services/accesscontrol.service";
import { filter } from "rxjs";
import { ShellAction, ShellActionService } from "../../../../services/shell-action.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ButtonUifComponent } from "../../../button-uif/button-uif.component";
import { PaginationUifComponent } from "../../../pagination-uif/pagination-uif.component";
import { FormBlockerUifComponent } from "../../../form-blocker-uif/form-blocker-uif.component";
import { SessionService } from "../../../../services/session.service";
import { TimemachineRouteDefs } from "../../../../../features/timemachine/routing/TimemachineRouteDefs";
import { UserSettingsService } from "../../../../services/usersettings.service";
import { FooterUifComponent } from "../../../footer-uif/footer-uif.component";
import { InfoboxDisplayUifComponent } from "../../../infobox-display-uif/infobox-display-uif.component";

@Component({
  selector: "app-shell-default-uif",
  imports: [
    ShellNavitemUifComponent,
    LucideAngularModule,
    CommonModule,
    InputSearchDropdownUifComponent,
    TranslocoDirective,
    PageErrorUifComponent,
    ShellBreadcrumbsUifComponent,
    ButtonUifComponent,
    PaginationUifComponent,
    FormBlockerUifComponent,
    FooterUifComponent,
    InfoboxDisplayUifComponent,
  ],
  templateUrl: "./shell-default-uif.component.html",
  styles: ``,
})
export class ShellDefaultUifComponent implements AfterViewInit, OnDestroy {
  navService = inject(NavService);
  shellService = inject(ShellService);
  // Für das Template
  filterItems = computed(() => this.navService.leafItems().map((i) => i.name));
  readonly hamburgerIcon = Menu;
  public favOpen = false;
  accessControlService = inject(AccessControlService);
  actionButtons = signal<ShellAction[]>([]);
  actionButtonsToRender = computed(() => this.actionButtons().filter((value) => value.render !== false));
  sessionService = inject(SessionService);
  timeMachineActive = computed(() => this.sessionService.getSessionInfo()()?.timeMachineActive ?? false);
  isAdmin = this.accessControlService.hasPermission("administratorDisplay");
  userSettingService = inject(UserSettingsService);
  @ViewChild("main", { static: false }) mainContent!: ElementRef;
  @ViewChild("breadcrumbs", { static: false }) breadCrumbs!: ElementRef;
  private router = inject(Router);
  private shellActionService = inject(ShellActionService);
  readonly scrollbarWidth = signal(0);

  private resizeObserver?: ResizeObserver;


  constructor() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.navService.openMenusForActiveRoute(this.router);
    });

    this.shellActionService.actions$.pipe(takeUntilDestroyed()).subscribe((actions: ShellAction[]) => {
      this.actionButtons.set(actions);
    });
  }

  onPaginationChange(newPagination: { page: number; pageSize: number }) {
    this.shellService.pagination.set({
      ...this.shellService.pagination(),
      page: newPagination.page,
      pageSize: newPagination.pageSize,
    });
  }

  executeAction(actionId: string): void {
    this.shellActionService.triggerAction(actionId);
  }

  onLogoClicked() {
    const homePath = HomeRouteDefs.getFQRHome();
    this.router.navigate([homePath]);
  }

  onSearchSelection(name: string) {
    const item = this.navService.leafItems().find((i) => i.name === name);
    if (item && item.route) this.router.navigate([item.route()]);
  }

  onToggleFavorite(item: NavDto) {
    if (item.route) {
      this.navService.toggleFavorite(item.route());
    }
  }

  ngAfterViewInit() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      setTimeout(() => {
        if (this.userSettingService.getUserSettingsData()()?.a11yFocusBreadcrumbsAfterNavigate) {
          if (this.breadCrumbs && this.breadCrumbs.nativeElement) {
            this.breadCrumbs.nativeElement.focus();
          }
        }

        if (this.userSettingService.getUserSettingsData()()?.a11yFocusMainContentAfterNavigate) {
          if (this.mainContent && this.mainContent.nativeElement) {
            this.mainContent.nativeElement.focus();
          }
        }
      });
    });

    const el = this.mainContent.nativeElement;

    const updateScrollbarState = () => {
      this.scrollbarWidth.set(
        el.offsetWidth - el.clientWidth
      );
    };

    // Initial check
    updateScrollbarState();

    // Watch for size changes
    this.resizeObserver = new ResizeObserver(() => {
      updateScrollbarState();
    });

    this.resizeObserver.observe(el);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  timemachineClicked() {
    if (this.timeMachineActive() && this.isAdmin.data()) {
      this.router.navigate([TimemachineRouteDefs.getFQTimeMachinePath()]);
    }
  }
}
